// @flow
import React from 'react';
import {
  Box,
  Checkbox,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  Divider,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Table,
  TableBody,
  TableHead,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  AlertInfoIcon,
  CheckIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
// eslint-disable-next-line max-len
import {
  green,
  red,
} from '@fortress-technology-solutions/fortress-component-library/design';
import useIntl from '../../../hooks/useIntl';
import { CONVENTIONAL_PROGRAM_NAME } from '../constants';
import messages from '../messages';

type Props = {
  programRows: Array<{
    name: string,
    waitlistRequired: boolean,
    eligible?: ?boolean,
    incomeLevel?: ?string,
    available?: boolean,
  }>,
  ReactHookFormProps: Object,
  isIncomeLevelError?: boolean,
  unavailableTooltip?: string,
};

const TABLE_SX = {
  '& .MuiTableHead-root, & .MuiTableRow-root.MuiTableRow-head, & .MuiTableRow-root:nth-of-type(odd)':
    { backgroundColor: 'transparent' },
  '& .MuiTableCell-root': { px: 0, py: 1.5 },
};

const CHECKBOX_SX = { p: 0, mr: 1 };

// The pair the Qualification Test's set-aside rows already colour a pass and a
// failure with, so one household reads the same on both screens.
const ELIGIBILITY_COLOR = { pass: green.dark, fail: red.light };

// The API answers a band by name. Rendering the string raw would leave these as
// the only untranslatable text in the table.
const INCOME_LEVEL_MESSAGE = {
  'Extremely Low': messages.incomeLevelExtremelyLow,
  'Very Low': messages.incomeLevelVeryLow,
  Low: messages.incomeLevelLow,
  Moderate: messages.incomeLevelModerate,
  'Over Income': messages.incomeLevelOverIncome,
  'Not Determined': messages.incomeLevelNotDetermined,
};

// A box ticked before its program was ruled out stays live, or the agent could
// never clear it. A disabled control fires no pointer events, so the tooltip
// hangs off a wrapper instead. The wrapper takes focus because the disabled box
// leaves the tab order, and the tooltip must stay reachable by keyboard.
const ProgramCheckbox = ({
  name,
  checked,
  available,
  unavailableTooltip,
  onToggle,
}: {
  name: string,
  checked: boolean,
  available: boolean,
  unavailableTooltip: string,
  onToggle: () => void,
}) => {
  const checkbox = (
    <Checkbox
      name={`selectedPrograms.${name}`}
      inputProps={{ 'aria-label': name }}
      checked={checked}
      disabled={!available && !checked}
      onChange={onToggle}
      sx={CHECKBOX_SX}
    />
  );

  if (available) {
    return checkbox;
  }

  const isFocusable = !checked;

  return (
    <Tooltip title={unavailableTooltip}>
      <span
        role={isFocusable ? 'group' : undefined}
        aria-label={isFocusable ? unavailableTooltip : undefined}
        tabIndex={isFocusable ? 0 : undefined}
      >
        {checkbox}
      </span>
    </Tooltip>
  );
};

const SelectedProgramsTable = ({
  programRows,
  ReactHookFormProps,
  isIncomeLevelError = false,
  unavailableTooltip = '',
}: Props) => {
  const intl = useIntl();
  const { Controller, control } = ReactHookFormProps;
  const placeholder = intl.formatMessage(messages.placeholder);

  // A failed score is not an unscored program: leaving both on the placeholder
  // would report 'this property published no limits' for a request that broke.
  const unscored = isIncomeLevelError
    ? intl.formatMessage(messages.incomeLevelUnavailable)
    : placeholder;

  const formatIncomeLevel = (incomeLevel: ?string) => {
    if (incomeLevel == null) {
      return unscored;
    }

    const message = INCOME_LEVEL_MESSAGE[incomeLevel];

    return message ? intl.formatMessage(message) : incomeLevel;
  };

  return (
    <Box>
      <Divider />
      <Typography variant="titleMedium" component="h4" my={2}>
        {intl.formatMessage(messages.selectedPrograms)}
      </Typography>
      <Controller
        name="selectedPrograms"
        control={control}
        // `validate` rather than `required`: this Controller is rendered by the
        // app rather than by the library `Form`, so nothing would supply the
        // copy for a bare `required`, which resolves to an empty message.
        rules={{
          validate: (value) =>
            Boolean(value?.length) ||
            intl.formatMessage(messages.atLeastOneProgram),
        }}
        render={({ field: { value, onChange }, fieldState }) => {
          const selectedPrograms = value ?? [];
          const toggleProgram = (name: string) =>
            onChange(
              selectedPrograms.includes(name)
                ? selectedPrograms.filter((program) => program !== name)
                : [...selectedPrograms, name],
            );

          return (
            <>
              <Table size="small" sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="labelLarge">
                        {intl.formatMessage(messages.program)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="labelLarge">
                        {intl.formatMessage(messages.eligibility)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="labelLarge">
                        {intl.formatMessage(messages.incomeLevel)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="labelLarge">
                          {intl.formatMessage(messages.waitlistRequired)}
                        </Typography>
                        <Tooltip
                          title={intl.formatMessage(
                            messages.waitlistRequiredTooltip,
                          )}
                        >
                          <AlertInfoIcon fontSize="small" color="action" />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programRows.map(
                    ({
                      name,
                      waitlistRequired,
                      eligible,
                      incomeLevel,
                      available = true,
                    }) => (
                      <TableRow key={name}>
                        <TableCell>
                          <Stack direction="row" alignItems="center">
                            <ProgramCheckbox
                              name={name}
                              checked={selectedPrograms.includes(name)}
                              available={available}
                              unavailableTooltip={unavailableTooltip}
                              onToggle={() => toggleProgram(name)}
                            />
                            <Typography variant="bodyMedium">{name}</Typography>
                          </Stack>
                        </TableCell>
                        {/* Neither Pass nor Fail is true of a program the
                          property has published no income limits for, so the
                          placeholder stays in the table's own colour. */}
                        <TableCell>
                          {eligible == null ? (
                            <Typography variant="bodyMedium">
                              {unscored}
                            </Typography>
                          ) : (
                            <Typography
                              variant="bodyMedium"
                              color={
                                eligible
                                  ? ELIGIBILITY_COLOR.pass
                                  : ELIGIBILITY_COLOR.fail
                              }
                            >
                              {intl.formatMessage(
                                eligible
                                  ? messages.eligibilityPass
                                  : messages.eligibilityFail,
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="bodyMedium">
                            {formatIncomeLevel(incomeLevel)}
                          </Typography>
                        </TableCell>
                        {/* Conventional units have no waitlist to require, so the
                          column reads as not applicable rather than as unmet. */}
                        <TableCell>
                          {name === CONVENTIONAL_PROGRAM_NAME ? (
                            <Typography variant="bodyMedium">
                              {placeholder}
                            </Typography>
                          ) : null}
                          {waitlistRequired ? (
                            <CheckIcon color="success" />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
              {fieldState.error ? (
                <Typography variant="bodyMedium" color="error">
                  {fieldState.error.message}
                </Typography>
              ) : null}
            </>
          );
        }}
      />
    </Box>
  );
};

export default SelectedProgramsTable;
